/** Shared by browser/mobile pickers, request validation and server model routing. */
export const aiModels = {
  "gpt-5.6-luna": {
    label: "GPT-5.6 Luna",
    provider: "openai",
    model: "gpt-5.6-luna",
    reasoningEffort: "medium",
    costBadge: "$",
  },
  "gpt-5.6-terra": {
    label: "GPT-5.6 Terra",
    provider: "openai",
    model: "gpt-5.6-terra",
    reasoningEffort: "medium",
    costBadge: "$$",
  },
  "gpt-5.6-sol": {
    label: "GPT-5.6 Sol",
    provider: "openai",
    model: "gpt-5.6-sol",
    reasoningEffort: "xhigh",
    costBadge: "$$$",
  },
  // On the 02.10.2026 nutrition comparison (medium effort) it fit macros best of six composers
  // at a third of gpt-5.6-sol's cost. Its xhigh run was never measured; Sol 5.6 on xhigh timed out.
  "gpt-6.1-sol": {
    label: "GPT-6.1 Sol",
    provider: "openai",
    model: "gpt-6.1-sol",
    reasoningEffort: "medium",
    costBadge: "$$",
  },
  "claude-opus-5-5": {
    label: "Claude Opus 5.5",
    provider: "anthropic",
    model: "claude-opus-5-5",
    reasoningEffort: "medium",
    costBadge: "$$$",
  },
  "gemini-3.8-flash": {
    label: "Gemini 3.8 Flash",
    provider: "google",
    model: "gemini-3.8-flash",
    reasoningEffort: undefined,
    costBadge: "$",
  },
} as const;

export type AiModelId = keyof typeof aiModels;
export type AiModelOption = { id: AiModelId } & (typeof aiModels)[AiModelId];

/** Internal stages are not additional user-selectable choices. */
export const aiInternalModels = {
  nutritionFast: "gemini-3.1-flash-lite",
  // one small call per generation, and the one every pool-wiping bug of TRE-2133/2163 started
  // in: the lite model turns "nie lubi" into a ban and "gotowane pomidory" into a tomato ban
  nutritionExtraction: aiModels["gemini-3.8-flash"].model,
  // TRE-2186 — not a language model: a decision model reached through the AI Gateway's evaluation
  // endpoint (typed questions in, probabilities out). It assesses the candidate pool; nutritionFast
  // is its fallback. It cannot take over the extraction above — that step writes labels and stems.
  nutritionAssessor: "typesafe-ai/jev",
  // TRE-2191 — decides waitingForResponse, isThreadContinuation and issueType for each client chat
  // message; chatAgent's LLM steps are the fallback. On 291 staging messages it matched the
  // reference better on waiting (90% vs 86%) and on the issue types that matter (90% vs 74%: the
  // lite model called 72 messages unhappy, the reference 14). The summary, sentiment and actionable
  // stay on the LLM, where Jev was no better.
  chatMessageAssessor: "typesafe-ai/jev",
  // TRE-2257 — "does this release announcement concern clients?" before the help changelog editor
  // runs on Opus. Only a confident "no" skips an announcement; everything else goes to the editor.
  helpChangelogTriage: "typesafe-ai/jev",
  nutritionLegacyFast: "gemini-2.5-flash",
  nutritionExpansion: aiModels["gpt-5.6-luna"].model,
  // TRE-2188 — the week composer's second attempt after the trainer's model failed or timed out:
  // gpt-5.6-sol on xhigh hit undici's 300 s headers timeout on 6 of 45 prod calls, and retrying the
  // same model doubled that. 30-day p90 of this one on the same step: 42 s, no errors.
  nutritionComposeFallback: aiModels["gemini-3.8-flash"].model,
} as const;

export const imageModels = {
  "google-nano-banana-lite": {
    label: "Nano Banana 2 Lite",
    provider: "google",
    model: "gemini-3.1-flash-lite-image",
    ownerOnly: false,
  },
  "google-nano-banana": {
    label: "Nano Banana 2",
    provider: "google",
    model: "gemini-3.1-flash-image",
    ownerOnly: false,
  },
  openai: { label: "GPT Image 2", provider: "openai", model: "gpt-image-2", ownerOnly: false },
  // Accepted for older clients; hidden from new selections. Keep its existing access restriction.
  "google-nano-banana-pro": {
    label: "Nano Banana Pro",
    provider: "google",
    model: "gemini-3-pro-image",
    ownerOnly: true,
  },
} as const;
export type ImageModelId = keyof typeof imageModels;

export function getAiProviderOptions(option: AiModelOption): Record<string, Record<string, string | boolean>> {
  if (option.provider === "openai") {
    // @ai-sdk/openai only treats gpt-5* as reasoning models and silently drops the effort for gpt-6*
    return {
      openai: {
        reasoningEffort: option.reasoningEffort,
        ...(option.model.startsWith("gpt-6") && { forceReasoning: true }),
      },
    };
  }
  // Opus 5.5 always thinks: effort is its only control and the API default is medium
  if (option.provider === "anthropic") return { anthropic: { effort: option.reasoningEffort } };
  return {};
}
